// The gift card receipt (/giftcard/receipt/<gift card>): the API's answer
// and what the page shows from it. Port of v1's GiftCardReceiptView. Pure.

import { Location__FullAddressString } from "@obby/lib";

export type GiftCardAddress = Record<string, string | undefined>;

/** GET /widget/pages/payment/giftcard/sucess/:id answers `{ giftcard }`:
 * the whole stored gift card, or null. Only what the page reads (never
 * its code). */
export type GiftCard = {
  _id?: string;
  buyerEmail?: string;
  recipient?: string;
  email?: string;
  message?: string;
  amount?: number;
  currencyUsed?: string;
  expiresAt?: string | null;
  address?: GiftCardAddress | null;
  details?: { cover?: string } | null;
  school?: string | { _id?: string } | null;
  widgetBoughtSchoolID?: string | null;
};

/** The buyer's message; v1's checkout stores "<empty>" for none. */
export const giftMessage = (card: GiftCard): string => (card.message === "<empty>" ? "" : (card.message ?? "").trim());

/** The postal address on one line ("" without one). */
export function giftAddress(card: GiftCard): string {
  const address = card.address;
  if (!address || typeof address !== "object") return "";
  return String(Location__FullAddressString(address) ?? "");
}

/** "31/12/2026" (v1's "DD/MM/YYYY"), in the browser's zone unless one is
 * given; "" without a date (v1's moment would have shown today). */
export function expiryDate(iso: string | null | undefined, timeZone?: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "2-digit", year: "numeric", timeZone }).format(d);
}

export type TimelineStep = { icon: "sent" | "mail" | "post"; title: string; text: string };

/** "What happens next", v1's three steps: the buyer's confirmation, the
 * recipient's email (when it is emailed), the post (when it is posted). */
export function giftTimeline(card: GiftCard): TimelineStep[] {
  const steps: TimelineStep[] = [
    { icon: "sent", title: "Confirmation email sent to you", text: `${card.buyerEmail ?? "You"} receives an email confirming the giftcard!` },
  ];
  if (card.email) {
    steps.push({
      icon: "mail",
      title: `${card.recipient || "Your recipient"} receives a giftcard`,
      text: `${card.email} receives an email with the gift card and instructions!`,
    });
  }
  const address = giftAddress(card);
  if (address) steps.push({ icon: "post", title: "Gift card posted", text: `We've sent the giftcard to ${address}` });
  return steps;
}
